import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { NS } from './U3146';
import {
  doClose,
  doPreview,
  doSaveInactiveDaysDefault,
  luHandleAppiError,
  parseU3146CloseResult,
  parseU3146PreviewResult,
  type U3146CloseRequest,
  type U3146CloseResult,
  type U3146PreviewRequest,
  type U3146PreviewResult,
} from './U3146Service';

export interface U3146CloseInactiveWizardHandlersParams {
  onError?: (error: unknown) => void;
}

export function useU3146CloseInactiveWizardHandlers(params: U3146CloseInactiveWizardHandlersParams = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const { onError } = params;

  const handleApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      onError?.(error);
    },
    [t, toast, onError],
  );

  const preview = useCallback(
    async (body: U3146PreviewRequest): Promise<U3146PreviewResult | null> => {
      try {
        const res = await doPreview(axios, body);
        return parseU3146PreviewResult(res.data);
      } catch (error: unknown) {
        handleApiError(error);
        return null;
      }
    },
    [axios, handleApiError],
  );

  const close = useCallback(
    async (body: U3146CloseRequest): Promise<U3146CloseResult | null> => {
      try {
        const res = await doClose(axios, body);
        return parseU3146CloseResult(res.data);
      } catch (error: unknown) {
        handleApiError(error);
        return null;
      }
    },
    [axios, handleApiError],
  );

  const saveInactiveDaysDefault = useCallback(
    async (inactiveDays: number): Promise<boolean> => {
      try {
        await doSaveInactiveDaysDefault(axios, inactiveDays);
        toast.success(t('msgInactiveDaysDefaultSaved', { ns: NS }));
        return true;
      } catch (error: unknown) {
        handleApiError(error);
        return false;
      }
    },
    [axios, handleApiError, toast, t],
  );

  return { preview, close, saveInactiveDaysDefault };
}
